// The ILRDF e-dicts store ONE aggregated entry per headword: every sense any
// contributor recorded, joined with ；, plus bracketed grammar-category codes
// (［虛］［主］［位］…) that are classifications, not meanings. That is fine for a
// dictionary and wrong for a reading popup — Yami 'ya' arrives as
// "他；它；主格；表眼前此時此刻，及時性，正在發生的事件；指示詞；這個；這是".
// Weighted by how often a word is actually READ (not by dictionary size),
// 21% of glossed taps land on a pile like that, and they are almost all the
// particles a learner taps most: rukai ku/ki/ka, yami na/no/ya, paiwan a.
//
// So fold, never guess: keep the entry's own order, drop the category codes and
// the senses already contained in an earlier one, show the first few, and leave
// the rest one tap away. Callers keep the RAW string for the gloss-edit prefill
// and the `was` field of a correction — this is a display transform only.

// ［虛］ and friends, including the unclosed truncations the truku file carries ("[介", "[虛").
const TAG = /^[［\[][^］\]]{0,4}[］\]]?$/;

const plain = (s) => s.replace(/[（(［\[][^）)］\]]*[）)］\]]?/g, '').trim() || s;

// 多 / 多的 / 很多 are one sense typed three ways, and the e-dicts carry all
// three side by side. Test a new sense against the stems it could be an
// elaboration of — deliberately one-way: only the ARRIVING sense is stripped,
// and what we have already kept goes into `seen` verbatim. Strip both ends and
// 太陽 would put 陽 in the set and swallow a later 陽 that is its own sense.
// 第六 vs 六 and 若是 vs 若 stay distinct either way: nothing strips 第 or 是.
const keys = (s) => {
  const out = new Set([s]);
  const strip = (t) => {
    const a = t.replace(/[的地]$/, '');
    if (a) out.add(a);
    const b = a.replace(/^(?:很|非常|十分|極為?|太)/, '');
    if (b) out.add(b);
  };
  strip(s);
  return out;
};

// The yami file separates senses with a bare full stop where every other file
// uses ； — 201 of its entries, so 'do' reads "格位標記.處格標記；處格標記；處所"
// with a period sitting inside the Chinese. Promote it before splitting, but
// only BETWEEN two Han characters, so a decimal (約 1.5 公尺) is left alone.
// Rewritten rather than matched with a lookbehind: this ships to phones, and a
// lookbehind is a PARSE error on iOS Safari before 16.4 — it would not fail the
// gloss, it would fail the whole bundle.
const DOT = /([一-鿿])\.([一-鿿])/g;

// A sense slot is not always a sense. The e-dicts file a particle's SYNTAX in
// the same ；-separated list as its meanings, so bunun 'masa' arrives as
// "當；談到某過去的時間（可以置於句首作為主題前標記）；［虛］" and the fold, having
// nothing to tell the three apart, keeps the description in the headline next
// to 當. Weighted by taps, this is the same population the tag-dropping was
// written for -- the markers a learner hits on every line (paiwan a ×136,
// bunun a ×128, rukai ku ×72) -- and for those the description is ALL the
// entry has, so it cannot simply be discarded either.
//
// Route it instead: meanings stay on the headline, descriptions become a 用法
// line, which is where the reader already looks for how a word behaves. Two
// gates, both deliberately narrow. A category term only counts when a case or
// clause word licenses it, because 標記 is also an ordinary verb (做記號) and a
// language with a word for it would lose the gloss. Prose verbs count on their
// own -- 表示, 用於, 置於 do not appear in a two-character meaning.
const CAT = /(主格|受格|賓格|賓語|屬格|斜格|處格|處所|方位|主題|通格|不定|格位|語氣|語助|發語|連繫|依附|完成|關係子句)[^；]{0,6}(標記|助詞|格|詞)/;
const DESC = /(標記|助詞|發語詞|連接詞|介系詞|繫詞|類別詞|虛詞|依附詞|冠詞|前綴|後綴|詞綴)/;
const PROSE = /(表示|表「|用於|用在|用以|置於|構成|指某|談到某|作為|等於)/;
// Not every demoted string is equally useless on a headline. A label that
// names a RELATION -- 受格標記, 處所格, 疑問助詞 -- is the best gloss a marker
// has, and can share the headline with the entry's meanings. One that only
// names a word class -- 虛詞, 助詞, 介系詞 -- says no more than 'this is a
// function word': paiwan 'a' reads better as 啊 than as 啊；虛詞.
const REL = /(主格|受格|賓格|賓語|屬格|斜格|處格|處所|方位|主題|通格|不定|格位|完成|疑問|關係子句|連繫|類別)[^；]{0,6}(標記|助詞|格|詞)/;
const isDesc = (s) => CAT.test(s) || DESC.test(s) || PROSE.test(s);

// The description often rides INSIDE a real sense rather than replacing it:
// amis 'i' is "在（介系詞）" and kavalan 'tu' is "在［斜］". Judging the whole
// string demoted both, and 在 -- the sense a learner needs -- vanished off the
// headline while 正在 stayed. Judge the text outside the brackets, and when
// only the bracket is descriptive, keep the meaning and let the bracket go to
// 用法. A qualifier that is not a description (六（數字）) is untouched, so the
// entries that already read well keep reading the way they do.
const stripTags = (s) => s.replace(/[［\[][^］\]]{0,4}[］\]]?/g, '').trim();

// peelNote takes a parenthesis off the END of an entry. The e-dicts also put
// one at the FRONT, naming what the word may be said of: bunun 'kulaaz' is
// "（健康/東西）壞", and as a 詞根 line that reads 詞根 kulaaz：（健康/東西）壞
// before the reader gets to 壞. The restrictor is real information and belongs
// under 用法, not in front of the meaning. Require the remainder to be a meaning
// in its own right, so "（連繫助詞）" -- all parenthesis, no remainder -- is left
// exactly as it is.
const LEAD = /^[（(]([^）)]{2,20})[）)](.+)$/;

// { text, desc, note } -- what to show for this sense, whether it is one at
// all, and anything peeled off it that the reader is still owed.
function weigh(part) {
  const tagless = stripTags(part);
  const core = plain(tagless);
  if (!core) return { text: part, desc: true, rel: REL.test(part) };
  if (isDesc(core)) return { text: part, desc: true, rel: REL.test(core) };
  const m = LEAD.exec(tagless);
  if (m && !isDesc(m[2])) return { text: m[2].trim(), desc: false, note: m[1].trim() };
  return { text: isDesc(tagless) ? core : tagless, desc: false };
}

// tsou 'ci' is a single parenthesis with a ； inside it -- "（繫詞／關係子句標記；
// covhi ci congeoha＝很遠的星球）" -- and splitting on every ； tore the example
// away from the label it illustrates, leaving a headline that opened with a
// stray bracket. Split at depth zero only.
function topSplit(s) {
  const out = []; let buf = '', depth = 0;
  for (const ch of s) {
    if (ch === '（' || ch === '(') depth++;
    else if (ch === '）' || ch === ')') depth = Math.max(0, depth - 1);
    if ((ch === '；' || ch === ';') && !depth) { out.push(buf); buf = ''; continue; }
    buf += ch;
  }
  out.push(buf);
  return out;
}

export function splitSenses(zh) {
  if (!zh) return [];
  const kept = [], seen = new Set();
  for (let part of topSplit(String(zh).replace(DOT, '$1；$2'))) {
    part = part.trim().replace(/[。\s]+$/, '');
    if (!part || TAG.test(part)) continue;
    // The same sense often appears several times over with different
    // qualifiers ("不是（否定詞）；不是［回答用］；不是"). Compare with the
    // qualifiers stripped, so those collapse — but keep 若 and 若是, or 六 and
    // 第六, which are different words and not one elaborating the other.
    const k = plain(part);
    let dup = false;
    for (const v of keys(k)) if (seen.has(v)) { dup = true; break; }
    if (dup) continue;
    seen.add(k);
    kept.push(part);
  }
  return kept;
}

// Split a cross-reference gloss ("參 mtzyuʼ：六", "參 詞根 …：…") into its label and
// the entry it points at, so the tail can be folded without eating the label.
const REF = /^((?:參|詞根)\s[^：]*：)([\s\S]+)$/;

// Hand-curated *_extra entries carry their evidence in a trailing parenthesis —
// which affix, which root, which line of the book. That is why the gloss can be
// trusted, and it is not what someone tapping a word wants to read: it buried
// 路過；經過 under its own restatement. Peel it off the headline and put it
// behind the same tap the extra senses use. Match from the END and require some
// length, so a qualifier that is part of the gloss (愛慕虛榮（的人）, 六（數）)
// stays where it belongs.
function peelNote(zh) {
  const s = String(zh).trim();
  const close = s.slice(-1);
  const open = close === '）' ? '（' : close === ')' ? '(' : '';
  if (!open) return { body: s, note: '' };
  let depth = 0, i = s.length - 1;
  for (; i >= 0; i--) {
    if (s[i] === close) depth++;
    else if (s[i] === open && !--depth) break;
  }
  if (i <= 0) return { body: s, note: '' };          // whole string is the parenthesis
  const body = s.slice(0, i).trim();
  const note = s.slice(i + 1, -1).trim();
  if (!body || note.length < 8) return { body: s, note: '' };
  return { body, note };
}

// The evidence for a gloss — which affix, which root, which line of the book —
// is not a second meaning, and a bracket at the end of the meaning is where a
// reader least expects to find it. Newer *_extra entries put it on its own
// line(s), so it can be LABELLED and rendered as one; peelNote stays for the
// ones still written the old way.
//
// One line per kind of evidence, because a word can need more than one: yami
// 'na' is BOTH a contraction of rana (構詞) and a particle whose sense depends on
// what precedes it (用法), and forcing that into a single labelled line meant
// mislabelling half of it. A line names its own label ("用法：…"); 構詞 is the
// default, since that is what most of these lines are.
const NOTE_LABEL = /^(.{1,4})：([\s\S]+)$/;

function splitNote(zh) {
  const nl = String(zh).indexOf('\n');
  if (nl < 0) {
    const { body, note } = peelNote(zh);
    return { body, notes: note ? [{ label: '構詞', text: note }] : [] };
  }
  const notes = [];
  for (const raw of zh.slice(nl + 1).split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = NOTE_LABEL.exec(line);
    notes.push(m ? { label: m[1], text: m[2].trim() } : { label: '構詞', text: line });
  }
  return { body: zh.slice(0, nl).trim(), notes };
}

// Is the e-dict entry behind the 詞典 fold worth a button? Only when neither
// side contains the other: an overlay that TIDIES the entry (兄 for 兄；哥哥)
// and an entry that merely restates the overlay both leave the reader nothing
// to open. That was always the rule; it was written as a substring test, which
// silently failed the moment splitSenses actually removed something — a
// duplicate sense, a ［虛］ tag — because `full` is then a filtered REWRITE of
// `book`, not a slice of it. So 詞典 opened onto the very line the reader was
// already looking at, with the noise put back: atayal 'ga' (15 taps) showed
// 主題標記；是；嗎（用在問句）… twice over. 121 word types across the 16 books
// were doing this, 553 taps' worth. Compare the sense SETS instead.
//
// Across two entries, 六 and 六（數字） are one sense typed twice — but
// splitSenses only collapses that INSIDE one string, via plain()/keys(). Fold
// the same way here, plus the trailing ellipsis the e-dicts use as a stub
// (saisiyat 'ray': 在（處所）；在...；在), so a restatement does not read as a
// difference.
const senseKey = (s) => plain(s).replace(/[…。.]+$/, '').replace(/[的地]$/, '').trim();

// Only ；separates senses for DISPLAY, but the tgdaya overlay writes a pair of
// near-synonyms with a 、inside one sense (叫做、所謂的), and the e-dict then
// carries just one half of it. Split on 、for the comparison alone, so half an
// overlay sense does not read as a sense the dictionary adds.
const parts = (s) => senseKey(s).split(/[、,，]/).map((x) => x.trim()).filter(Boolean);

const covers = (a, b) => {                    // every sense of b is already in a
  const have = new Set();
  for (const s of a) { have.add(senseKey(s)); for (const x of parts(s)) have.add(x); }
  return b.every((s) => have.has(senseKey(s)) || parts(s).every((x) => have.has(x)));
};

export function dictSaysMore(book, full) {
  if (!book || !full) return false;
  const b = splitSenses(book), f = splitSenses(full);
  if (!b.length || !f.length) return false;
  return !covers(b, f) && !covers(f, b);
}

// Put the demoted senses in front of a 用法 the entry already has, rather than
// adding a second line under the same label.
function merge(text, notes) {
  const i = notes.findIndex((n) => n.label === '用法');
  if (i < 0) return [{ label: '用法', text }, ...notes];
  const out = notes.slice();
  out[i] = { ...out[i], text: `${text}。${out[i].text}` };
  return out;
}

// { head, full, more, notes } — head is the collapsed line, full is every sense
// with the notes taken off (what expanding shows), more is how many senses head
// hides, notes are the labelled lines under it.
export function shortGloss(zh, limit = 3) {
  if (!zh) return { head: '', full: '', more: 0, notes: [] };
  const { body, notes } = splitNote(zh);
  const m = REF.exec(body);
  const label = m ? m[1] : '';
  const senses = splitSenses(m ? m[2] : body);
  if (!senses.length) return { head: body, full: body, more: 0, notes, all: body };
  // Descriptions off the headline and onto 用法 -- unless they are the whole
  // entry, in which case there is no meaning to promote and the reader is
  // better served by the description than by a blank. Written ahead of any
  // 用法 the entry already carries, since it is the more general statement.
  const w = senses.map(weigh);
  // For a word that IS a marker, the category is the best gloss it has: atayal
  // 'ga' opens 主題標記；是；嗎, and demoting that to promote 是 would be a worse
  // gloss, not a better-balanced one. The e-dicts do not order senses by
  // importance, though -- rukai 'ki' lists 受格標記 second, behind 那裡的, and
  // reading only 那裡的；的；是 says nothing about what the word does. So the
  // first category keeps the headline wherever the entry put it, and later ones
  // fold. Only a relation label qualifies, so bunun 'masa' still sheds its
  // 談到某過去的時間… wherever the entry lists it, and sakizaya 'tu' promotes its
  // 受格標記 rather than the 介系詞 that happens to precede it.
  const keep = w.findIndex((x) => x.desc && x.rel);
  const show = w.filter((x, i) => i === keep || !x.desc).map((x) => x.text);
  // A sense kept only after its bracket was peeled still owes the reader the
  // bracket, so carry it to 用法 alongside the demoted ones.
  const peeled = senses.filter((x, i) => !w[i].desc && !w[i].note && w[i].text !== stripTags(x));
  const use = show.length
    ? [...senses.filter((_, i) => i !== keep && w[i].desc), ...peeled,
       ...w.filter((x) => x.note).map((x) => x.note)]
    : [];
  if (!show.length) show.push(...w.map((x) => x.text));
  const all = label + senses.join('；');
  return {
    head: label + show.slice(0, limit).join('；'),
    full: label + show.join('；'),
    more: Math.max(0, show.length - limit),
    // A curated entry may already carry its own 用法 line. Two boxes both
    // labelled 用法 read as a rendering fault, so fold the demoted senses into
    // the front of the one that is there -- saisiyat 'ka' has both.
    notes: use.length ? merge(use.join('；'), notes) : notes,
    all,
  };
}
